import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function About() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 按摩视频大全',
    description: '按摩视频大全品牌介绍、核心价值与联系方式',
    about: {
      '@type': 'Organization',
      name: '按摩视频大全',
      description: '专业按摩视频教程平台',
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotValue = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();

    if (honeypotValue) {
      setFormStatus('success');
      setFormMessage('提交成功，我们会尽快回复您！');
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      formData.delete('phone_alt');

      const response = await fetch('https://readdy.ai/api/form/d9q2jhnnahb7382ov55g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('提交成功，我们会尽快回复您！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormStatus('error');
        setFormMessage(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试');
    }
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>

      <Navbar />
      <ScrollToTop />

      <main className="pt-20">
        {/* Header */}
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={[
              { label: t('breadcrumbHome'), href: '/' },
              { label: t('aboutTitle') },
            ]} />

            <div className="px-4 md:px-6 py-10 md:py-14">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
                {t('aboutTitle')}
              </h1>
              <p className="text-sm md:text-base text-foreground-500">
                {t('aboutSubtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Brand Story */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
              {t('aboutBrandTitle')}
            </h2>
            <div className="prose-custom">
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                {t('aboutBrandContent')}
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                从最初的几个基础按摩视频到现在涵盖全身放松、穴位按摩、肩颈腰背、足底反射区、头部按摩等六大分类，我们始终坚持内容质量第一的原则。每一个教程视频都经过反复推敲和精心制作，确保用户能够准确理解每个按摩手法的要领。
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                我们相信，健康的身体是一切美好生活的基础。通过我们的按摩视频教程，让专业的按摩知识走进千家万户，帮助更多人缓解身体疲劳、改善亚健康状态，享受健康快乐的生活。
              </p>
            </div>
          </div>
        </section>

        {/* Mission */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
              {t('aboutMissionTitle')}
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto">
              {t('aboutMissionContent')}
            </p>
          </div>
        </section>

        {/* Values */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-10 text-center">
              {t('aboutValuesTitle')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {[
                { icon: 'ri-shield-check-line', title: t('aboutValue1Title'), desc: t('aboutValue1Desc') },
                { icon: 'ri-lightbulb-line', title: t('aboutValue2Title'), desc: t('aboutValue2Desc') },
                { icon: 'ri-refresh-line', title: t('aboutValue3Title'), desc: t('aboutValue3Desc') },
                { icon: 'ri-heart-line', title: t('aboutValue4Title'), desc: t('aboutValue4Desc') },
              ].map((value, i) => (
                <div key={i} className="text-center p-5">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                    <i className={`${value.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {value.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation / Testimonials */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-10 text-center">
              {t('aboutReputationTitle')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {[
                {
                  text: '这个网站的按摩视频教程非常用心！我按照足底反射区的教程每天帮妈妈按摩，她的睡眠质量明显改善了。视频讲解很详细，穴位定位也很准确，非常适合零基础学习。',
                  name: '刘先生',
                  role: '孝顺的儿子',
                },
                {
                  text: '作为程序员，肩颈问题困扰了我好几年。跟着肩颈按摩教程练习了两周，肩膀确实轻松了很多。最满意的是教程分步骤讲解，每个动作的力度和时间都有明确的说明。',
                  name: '陈先生',
                  role: '软件工程师',
                },
                {
                  text: '我是瑜伽老师，推荐了很多学员来这个网站学习按摩。教程内容专业系统，从基础理论到实操手法都有覆盖。特别是穴位按摩教学那个系列，讲得比很多线下课程还清楚。',
                  name: '林女士',
                  role: '瑜伽教练',
                },
              ].map((item, i) => (
                <div key={i} className="bg-background-50 rounded-lg p-5 md:p-6">
                  <div className="flex items-center gap-1 text-primary-400 mb-4">
                    {[...Array(5)].map((_, j) => (
                      <i key={j} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    "{item.text}"
                  </p>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{item.name}</p>
                    <p className="text-xs text-foreground-500">{item.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact-form" className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                {t('contactTitle')}
              </h2>
              <p className="text-sm text-foreground-500">
                {t('contactSubtitle')}
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="space-y-5"
            >
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  {t('formName')}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入您的姓名"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  {t('formEmail')}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入您的邮箱"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  {t('formMessage')}
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                  placeholder="请输入您的留言内容（500字以内）"
                ></textarea>
              </div>

              <div className="honeypot-field">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                {t('formSubmit')}
              </button>

              {formStatus === 'success' && (
                <div className="p-4 bg-secondary-100 text-secondary-800 rounded-lg text-sm">
                  <i className="ri-check-line mr-2"></i>
                  {formMessage}
                </div>
              )}

              {formStatus === 'error' && (
                <div className="p-4 bg-accent-100 text-accent-800 rounded-lg text-sm">
                  <i className="ri-error-warning-line mr-2"></i>
                  {formMessage}
                </div>
              )}
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}